import { FieldTypeEnum } from '../custom-field.types'
import { ModuleColumnDefinition } from '../module'
import { EntityTerminology } from '../organization-settings'
import {
  QUOTE_STATUS_COLORS,
  QUOTE_STATUS_DISPLAY_NAMES,
  QUOTE_STATUSES,
  QuoteListItemDTO,
} from '../quotes'

/**
 * Standard column definitions for the Quote module.
 * These correspond directly to the fields in QuoteListItemDTO.
 *
 * MONEY IS CENT-MATH: *Cents columns hold integer cents, so the table layer
 * must override their cells with a cents formatter (raw NUMBER rendering
 * would show 90000 for $900.00). Same reason `number` gets a Q-0042
 * override.
 *
 * Open quotes (DRAFT/SENT/VIEWED/EXPIRED) take inline edits, including
 * manual status transitions; ACCEPTED/DECLINED/VOID rows are immutable (the
 * table gates per-row, the API enforces). Derived lifecycle fields (sentAt,
 * signer*, totals, ...) stay isReadOnly — they only change through the
 * send/sign flows and line-item editor.
 *
 * @see QuoteListItemDTO for the full data structure
 */
export const QUOTE_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'name',
    name: 'name' as keyof QuoteListItemDTO,
    label: 'Quote Name',
    description: 'Name of the quote',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '250px',
    minWidth: '200px',
  },
  {
    id: 'number',
    name: 'number' as keyof QuoteListItemDTO,
    label: 'Quote #',
    description: 'Per-organization quote number (Q-0042)',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 1,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '120px',
    minWidth: '100px',
  },
  {
    id: 'status',
    name: 'status' as keyof QuoteListItemDTO,
    label: 'Status',
    description: 'Lifecycle status of the quote',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '130px',
    minWidth: '100px',
    options: QUOTE_STATUSES.map(status => ({
      value: status,
      label: QUOTE_STATUS_DISPLAY_NAMES[status],
      color: QUOTE_STATUS_COLORS[status],
    })),
  },
  {
    id: 'entityId',
    name: 'entityId' as keyof QuoteListItemDTO,
    label: 'Entity',
    description: 'Client entity for this quote',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '180px',
    minWidth: '150px',
  },
  {
    id: 'dealId',
    name: 'dealId' as keyof QuoteListItemDTO,
    label: 'Deal',
    description: 'Deal this quote was built from',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 4,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '180px',
    minWidth: '150px',
  },
  {
    id: 'contactId',
    name: 'contactId' as keyof QuoteListItemDTO,
    label: 'Contact',
    description: 'Recipient contact for this quote',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '180px',
    minWidth: '150px',
  },
  {
    id: 'totalCents',
    name: 'totalCents' as keyof QuoteListItemDTO,
    label: 'Total',
    description: 'Total amount of the quote',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    isReadOnly: true,
    order: 6,
    fieldType: FieldTypeEnum.enum.NUMBER,
    width: '130px',
    minWidth: '100px',
  },
  {
    id: 'subtotalCents',
    name: 'subtotalCents' as keyof QuoteListItemDTO,
    label: 'Subtotal',
    description: 'Subtotal before discount and tax',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    isReadOnly: true,
    order: 7,
    fieldType: FieldTypeEnum.enum.NUMBER,
    width: '130px',
    minWidth: '100px',
  },
  {
    id: 'discountCents',
    name: 'discountCents' as keyof QuoteListItemDTO,
    label: 'Discount',
    description: 'Discount amount',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    isReadOnly: true,
    order: 8,
    fieldType: FieldTypeEnum.enum.NUMBER,
    width: '120px',
    minWidth: '100px',
  },
  {
    id: 'taxCents',
    name: 'taxCents' as keyof QuoteListItemDTO,
    label: 'Tax',
    description: 'Tax amount',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    isReadOnly: true,
    order: 9,
    fieldType: FieldTypeEnum.enum.NUMBER,
    width: '110px',
    minWidth: '90px',
  },
  {
    id: 'validUntil',
    name: 'validUntil' as keyof QuoteListItemDTO,
    label: 'Expires',
    description: 'Date the quote stops accepting signatures',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 10,
    fieldType: FieldTypeEnum.enum.DATE,
    width: '140px',
    minWidth: '120px',
  },
  {
    id: 'sentAt',
    name: 'sentAt' as keyof QuoteListItemDTO,
    label: 'Sent',
    description: 'When the quote was sent for signature',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 11,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '180px',
    minWidth: '120px',
  },
  {
    id: 'viewedAt',
    name: 'viewedAt' as keyof QuoteListItemDTO,
    label: 'Viewed',
    description: 'When the recipient first opened the quote',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 12,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '180px',
    minWidth: '120px',
  },
  {
    id: 'respondedAt',
    name: 'respondedAt' as keyof QuoteListItemDTO,
    label: 'Responded',
    description: 'When the recipient accepted or declined',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 13,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '180px',
    minWidth: '120px',
  },
  {
    id: 'signerName',
    name: 'signerName' as keyof QuoteListItemDTO,
    label: 'Signed By',
    description: 'Name of the person who signed the quote',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 14,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '160px',
    minWidth: '130px',
  },
  {
    id: 'signerEmail',
    name: 'signerEmail' as keyof QuoteListItemDTO,
    label: 'Signer Email',
    description: 'Email of the person who signed the quote',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 15,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '180px',
    minWidth: '150px',
  },
  {
    id: 'currency',
    name: 'currency' as keyof QuoteListItemDTO,
    label: 'Currency',
    description: 'Currency of the quote',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    isReadOnly: true,
    order: 16,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '100px',
    minWidth: '80px',
  },
  {
    id: 'declineReason',
    name: 'declineReason' as keyof QuoteListItemDTO,
    label: 'Decline Reason',
    description: 'Reason given when the quote was declined',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 17,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '200px',
    minWidth: '150px',
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof QuoteListItemDTO,
    label: 'Created',
    description: 'Date when the quote was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 18,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '180px',
    minWidth: '120px',
  },
  {
    id: 'updatedAt',
    name: 'updatedAt' as keyof QuoteListItemDTO,
    label: 'Updated',
    description: 'Date when the quote was last updated',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 19,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '180px',
    minWidth: '120px',
  },
]

/**
 * Standard quote columns with the org's entity terminology applied to the
 * entity column label/description. Falls back to the default columns.
 */
export function createQuoteStandardColumns(
  terminology?: EntityTerminology,
): ModuleColumnDefinition[] {
  if (!terminology) return QUOTE_STANDARD_COLUMNS
  const Singular =
    terminology.singular.charAt(0).toUpperCase() + terminology.singular.slice(1)
  return QUOTE_STANDARD_COLUMNS.map(column =>
    column.id === 'entityId'
      ? {
          ...column,
          label: Singular,
          description: `${Singular} for this quote`,
        }
      : column,
  )
}
